/**
 * @fileoverview Provides a streaming utility for parsing and printing AI-generated text with special handling for code blocks. Manages the state of code and text sections, enabling dynamic printing of streamed content with proper formatting and context switching.
 */

import { print_ai_stream, print_ai_stream_code } from '@iterator/shared-node/util/logging'

export class AiStreamer {
  private currentlyCode = false
  private buffer = ''

  constructor(
    private printText: (chunk: string) => void = print_ai_stream,
    private printCode: (chunk: string) => void = print_ai_stream_code,
  ) {}

  private printChunk(chunk: string) {
    if (this.currentlyCode) {
      this.printCode(chunk)
    } else {
      this.printText(chunk)
    }
    this.buffer += chunk
  }

  stream(chunk: string) {
    let chunkLeft = chunk

    while (chunkLeft.includes('```')) {
      const possibleCodeBlockIndex = chunkLeft.indexOf('```')
      const chunkStartsWithCodeBlock = possibleCodeBlockIndex === 0
      const shouldToggleIsCode =
        chunkStartsWithCodeBlock || chunkLeft[possibleCodeBlockIndex - 1] === '\n'

      if (shouldToggleIsCode) {
        if (chunkStartsWithCodeBlock && this.currentlyCode) {
          // Close a code block.
          this.printChunk(chunkLeft.slice(0, 3))
          this.currentlyCode = !this.currentlyCode
          chunkLeft = chunkLeft.slice(3)
        } else if (chunkStartsWithCodeBlock && !this.currentlyCode) {
          // Open a code block.
          this.currentlyCode = true
          this.printChunk(chunkLeft.slice(0, 3))
          chunkLeft = chunkLeft.slice(3)
        } else {
          // Print up to the next code block. Close the block if possible.
          const toPrint = chunkLeft.slice(0, possibleCodeBlockIndex + (this.currentlyCode ? 3 : 0))
          this.printChunk(toPrint)

          // We've printed up to a code block and included it if it was a closing code block. Therefore,
          // we're no longer in a code block.
          this.currentlyCode = false
          chunkLeft = chunkLeft.slice(toPrint.length)
        }
      } else {
        // Not toggling whether it is code. The chunk may still include triple backticks.
        const toPrintIndex = chunkLeft.indexOf('```') + 3
        this.printChunk(chunkLeft.slice(0, toPrintIndex))
        chunkLeft = chunkLeft.slice(toPrintIndex)
      }
    }

    this.printChunk(chunkLeft)
  }
}
